import { useCallback, useEffect } from "react";

import {
  BusinessFilter,
  createFolderBusinessFilter,
  createFullTextBusinessFilter,
  DataViewKey,
  KpkAutoComplete,
  KpkAutoCompleteType,
  SentryBreadcrumbCategory,
  useFilterAdd,
} from "@keepeek/commons";
import * as Sentry from "@sentry/nextjs";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValueLoadable } from "recoil";
import { useDebounce } from "use-debounce";

import { CONFIGURATION_SECTION_PATH } from "../../components/layout/adminMenu";
import { Autocomplete } from "../../models/configuration/components/autocomplete";
import { useActiveFiltersFromURL } from "../businessFilter/hooks/useActiveFiltersFromURL";
import { useConfiguration } from "../config/hooks";
import { fieldIdText, UseSearchInput, useSearchInput } from "../searchInput/hooks";
import { autoCompleteQueryState, autoCompleteState, queryState } from "./atoms";
import { autocompleteSelector } from "./selectors";

export type UseSearchAutocompleteProps = {
  widgetId: string;
  resetSearch: boolean;
  limit?: number;
  fields: string[] | undefined;
  dataViewKey: DataViewKey;
};

export function useSearchAutocomplete({
  widgetId,
  limit = 50,
  resetSearch = false,
  fields,
  dataViewKey,
}: UseSearchAutocompleteProps): {
  handleQueryUpdate: (query: string) => void;
  selectOption: (kpkAutoComplete: KpkAutoComplete) => void;
  autoCompleteVals: KpkAutoComplete[] | null;
  query: string;
  loading: boolean;
} & UseSearchInput {
  const { setActiveFilters } = useActiveFiltersFromURL();
  const addFilter = useFilterAdd(dataViewKey);
  const { kSplitValue, setKSplitValue, kSplitValues, onSearch } = useSearchInput({
    fields,
    widgetId,
    dataViewKey,
  });
  const { t } = useTranslation();
  const autocompleteConfig = useConfiguration<Autocomplete>(
    CONFIGURATION_SECTION_PATH.COMPONENTS_AUTOCOMPLETE,
  );
  const router = useRouter();

  // Query used to show the text in the field in sync
  const [query, setQuery] = useRecoilState<string>(queryState(widgetId));
  // Query used to make search on auto complete API
  const [autoCompleteQuery, setAutoCompleteQuery] = useRecoilState<string>(
    autoCompleteQueryState(widgetId),
  );
  // Query debounced to avoid calling the API Too much during typing
  const [debouncedAutoCompleteQuery] = useDebounce(autoCompleteQuery, 300);
  // Make the autocomplete search with cache on query, folder, fields and limit
  const autoCompleteLoadable = useRecoilValueLoadable<KpkAutoComplete[] | null>(
    autocompleteSelector({
      query: debouncedAutoCompleteQuery,
      folderIds: autocompleteConfig?.folderIds,
      fieldIds: autocompleteConfig?.fieldIds,
      limit,
    }),
  );
  // Prompt the loading state when we're loading data from API but also when we're debouncing so the user know that
  // what he is typing will give another result
  const loading =
    autoCompleteLoadable.state === "loading" || debouncedAutoCompleteQuery !== autoCompleteQuery;

  const [autoCompleteVals, setAutoCompleteVals] = useRecoilState<KpkAutoComplete[] | null>(
    autoCompleteState(widgetId),
  );

  // Store the values in an atom to keep them when we're typing another keyword
  useEffect(() => {
    if (autoCompleteLoadable.state === "hasValue") {
      setAutoCompleteVals(autoCompleteLoadable.contents);
    }
  }, [autoCompleteLoadable.contents, autoCompleteLoadable.state, setAutoCompleteVals]);

  function handleQueryUpdate(query: string) {
    Sentry.addBreadcrumb({
      category: SentryBreadcrumbCategory.USER_INTERACTION,
      message: `useSearchAutocomplete - handleQueryUpdate: ${query}`,
    });
    const value = query && query.length >= 1 ? query : "";
    // Autocomplete only on more than 1 char
    // And only if kSplitValue is text or undefined
    setAutoCompleteQuery(
      (kSplitValue === undefined || kSplitValue.id === fieldIdText) && value.length > 1
        ? value
        : "",
    );
    // Keep the query in sync
    setQuery(value);
  }

  const _handleSetActiveFilters = useCallback(
    (filter: BusinessFilter) => {
      if (resetSearch) {
        return setActiveFilters([filter]);
      } else {
        return addFilter(filter);
      }
    },
    [addFilter, resetSearch, setActiveFilters],
  );

  const selectOption = useCallback(
    (kpkAutoComplete: KpkAutoComplete) => {
      Sentry.addBreadcrumb({
        category: SentryBreadcrumbCategory.USER_INTERACTION,
        message: `useSearchAutocomplete - selectOption: ${JSON.stringify(kpkAutoComplete)}`,
      });
      // Reset state when selecting an autocomplete value (will affect page navigation)
      setAutoCompleteQuery("");
      setQuery("");
      if (kpkAutoComplete?.type === KpkAutoCompleteType.Folder) {
        _handleSetActiveFilters(
          createFolderBusinessFilter(
            parseInt(kpkAutoComplete.id),
            kpkAutoComplete.label,
            t("business-filter.advanced.folder.label"),
          ),
        );
      } else {
        _handleSetActiveFilters(
          createFullTextBusinessFilter(kpkAutoComplete.label, t("business-filter.full-text.label")),
        );
      }
    },
    [_handleSetActiveFilters, setAutoCompleteQuery, setQuery, t],
  );

  // clear the query when we change the page
  useEffect(() => {
    setAutoCompleteQuery("");
    setQuery("");
  }, [router.route, setAutoCompleteQuery, setQuery]);

  return {
    handleQueryUpdate,
    selectOption,
    autoCompleteVals,
    query,
    loading,
    onSearch,
    kSplitValue,
    kSplitValues,
    setKSplitValue,
  };
}
